<template>
  <div class="bSummary">
    <div class="app__title app__title--h4 font-poppins">
      {{ partnerStore?.partner?.is_manufacturer_page ? t("buyout.detail.buyout_device") : t("buyout.appraisal.your_device") }}
    </div>
    <div class="app__divider" />
    <div class="bSummary__device">
      <img
        :src="buildImageUrl(selectedProductColor?.image ? selectedProductColor?.image : '')"
        class="img-responsive"
      >
      <div>
        <div class="font-weight-semibold">
          {{ selectedProductColor?.name }}
        </div>
        <v-btn
          class="link color--redTone1 cursor-pointer"
          variant="text"
          @click="changeClick"
        >
          {{ t("buyout.appraisal.change") }}
        </v-btn>
      </div>
    </div>
    <div class="app__divider" />
    <div class="bSummary__state">
      <div>{{ t("buyout.appraisal.device_state") }}</div>
      <div class="color--greenTone1 text-right">
        <div>{{ selectedProductQuality?.quality?.name }}</div>
        <div>
          {{ useCurrency(selectedProductQualityPrice) }}
        </div>
      </div>
    </div>
    <template v-if="usableBuyoutAccessories.length">
      <div class="app__divider" />
      <div class="bSummary__surcharge">
        <template v-for="accessory in usableBuyoutAccessories" :key="accessory.id">
          <div>{{ `${accessory?.quantity}x ${accessory.fe_summary_description}` }}</div>
          <div v-if="accessory?.quantity" class="color--greenTone1 text-right">
            {{ useCurrency(accessory.price * accessory.quantity) }}
          </div>
        </template>
      </div>
    </template>
    <template v-if="defectsAnswers?.length && usableDefects">
      <div class="app__divider" />
      <div v-if="defectsAnswers" class="bSummary__surcharge">
        <template v-for="answer in defectsAnswers" :key="answer.id">
          <template v-if="answer?.selectedValue?.value && answer.selectedValue.value > 0">
            <div>{{ answer.defect_text }}</div>
            <div class="color--redTone1 text-right">
              -{{ useCurrency(answer.selectedValue!.value) }}
            </div>
          </template>
        </template>
      </div>
    </template>
    <template v-if="buyoutStore.selectedBuyoutBonuses?.length && buyoutStore.selectedBuyoutBonuses.length > 0">
      <div class="app__divider" />
      <div class="bSummary__bonus">
        <div>
          {{ isNewDeviceProcess && bonusPartnerWithManufacturerBuyoutStore?.selectedBonusPartner?.fe_name ? `${t("buyout.appraisal.buyout_bonus_for_new")} ${bonusPartnerWithManufacturerBuyoutStore.selectedBonusPartner!.fe_name}` : t("buyout.appraisal.buyout_bonus") }}
          <div
            v-if="firstBonusWithRulesIndex > -1"
            class="app__label color--grayTone3 font-weight-regular text-decoration-underline cursor-pointer"
            @click="openFile(buyoutStore.selectedBuyoutBonuses[firstBonusWithRulesIndex])"
          >
            {{ t('buyout.appraisal.conditions') }}
          </div>
        </div>
        <div class="color--greenTone1 text-right">
          {{ useCurrency(selectedBonusesPrice) }}
        </div>
        <!-- ToDO has to be changed to loop when more bonuses for one buyout will be available-->
      </div>
    </template>
    <div class="app__divider" />
    <div class="bSummary__total font-weight-semibold">
      <div v-if="partnerStore!.getBuyoutPartner?.seo_name == 'O2'">
        {{ t("buyout.appraisal.total_receive") }}
      </div>
      <div v-else>
        {{ t("buyout.appraisal.total_buyout_price") }}
      </div>
      <div
        :class="[
          'app__title app__title--h4 font-poppins text-right',
          (qualityWithAccessoriesPrice - defectsValue) < minimumBuyoutPrice ? 'color--redTone2' : 'color--greenTone1'
        ]"
      >
        {{ useCurrency(buyoutTotalPrice) }}
        <v-tooltip
          v-if="(qualityWithAccessoriesPrice - defectsValue) < minimumBuyoutPrice"
          location="top"
        >
          <template #activator="{ props }">
            <v-icon
              v-bind="props"
              icon="ct:circle-exclamation"
            />
          </template>
          <span>{{ t('buyout.appraisal.price_can_not_fall_bellow_minimal') }}</span>
        </v-tooltip>
      </div>
      <v-btn
        v-if="continueBtn && isNewDeviceProcess && (!authStore.isPartner || authStore.isPartnerBrandStore)"
        :loading="!bonusesLoaded || continueButtonLoading"
        :disabled="!bonusesLoaded"
        class="mbtn mbtn--green"
        size="x-large"
        @click="moveToPersonalData"
      >
        {{ t('buyout.appraisal.continue') }}
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">

import { storeToRefs } from "pinia";
import { sumBy } from "lodash-es";
import useCurrency from "cashtec-common/src/composables/useCurrency";

const runtimeConfig = useRuntimeConfig();
const buyoutStore = useBuyoutStore();
const bonusPartnerWithManufacturerBuyoutStore = useBonusPartnerWithManufacturerBuyoutStore();
const partnerStore = usePartnerStore();
const authStore = useAuthStore();
const { buildImageUrl } = useUtils();
const { t } = useI18n();
const continueButtonLoading = ref<boolean>(false);

const componentProps = withDefaults(defineProps<{
  isNewDeviceProcess?: boolean
  continueBtn?: boolean
}>(), {
  isNewDeviceProcess: false,
  continueBtn: false
});

const {
  selectedProductColor,
  selectedProductQuality,
  defectsAnswers,
  accessories
} = storeToRefs(buyoutStore);

const usableDefects = computed<boolean>(() => {
  return defectsAnswers.value.some(answer => answer?.selectedValue?.value && answer.selectedValue.value > 0);
});

const usableBuyoutAccessories = computed<Accessory[]>(() => {
  return accessories.value.filter(ua => ua?.quantity && ua.quantity > 0);
});

const accessoriesValue = computed<number>(() => {
  return sumBy(usableBuyoutAccessories.value, (ua: Accessory) => (ua?.quantity || 0) * (ua?.price || 0));
});

const minimumBuyoutPrice = computed<number>(() => {
  return (runtimeConfig.public.minimumBuyoutPrice || 0) as number;
});

const bonusesLoaded = computed<boolean>(() => {
  return Boolean(buyoutStore?.selectedBuyoutBonuses?.length && buyoutStore.selectedBuyoutBonuses!.length > 0);
});

const defectsValue = computed<number>(() => {
  let tmpValue: number = 0;
  defectsAnswers.value.forEach((da: EnhancedBuyoutDefect) => {
    tmpValue += da!.selectedValue!.value;
  });
  return tmpValue;
});

const firstBonusWithRulesIndex = computed<number>(() => {
  return buyoutStore?.selectedBuyoutBonuses?.length ? buyoutStore.selectedBuyoutBonuses.findIndex(bonus => bonus?.rules) : -1;
}

);

const selectedProductQualityPrice = computed<number>(() => {
  return selectedProductQuality?.value?.buying_price ? selectedProductQuality.value.buying_price : 0;
});

const qualityWithAccessoriesPrice = computed<number>(() => {
  return selectedProductQualityPrice.value + accessoriesValue.value;
});

const selectedBonusesPrice = computed<number>(() => {
  return buyoutStore.selectedBuyoutBonuses ? sumBy(buyoutStore.selectedBuyoutBonuses, "bonus_final_value") : 0;
});

const buyoutTotalPrice = computed<number>(() => {
  const qualityPrice = qualityWithAccessoriesPrice.value - defectsValue.value > minimumBuyoutPrice.value ? selectedProductQualityPrice.value + accessoriesValue.value - defectsValue.value : minimumBuyoutPrice.value;
  return selectedBonusesPrice.value + qualityPrice;
});

function openFile (bonus: Bonus) {
  window.open(String(bonus!.rules), "_blank");
}

function changeClick () {
  buyoutStore.appraisalBuyoutStep = 2;
  if(componentProps.isNewDeviceProcess) {
    buyoutStore.mainBuyoutStep = 1;
  } else {
    buyoutStore.mainBuyoutStep = 0;
  };
}

async function moveToPersonalData(){
  if(!componentProps.isNewDeviceProcess) { return;}
  continueButtonLoading.value = true;
  await bonusPartnerWithManufacturerBuyoutStore.moveToPersonalData();
  continueButtonLoading.value = false;
}

</script>
